<template>
  <v-card class="elevation-0">
    <v-container fluid>
      <v-row>
        <v-spacer></v-spacer>
        <v-col cols="10">
          <v-form ref="tierSetupForm" v-model="valid" lazy-validation>
            <v-row>
              <v-col cols="12" class="d-flex align-center">
                <span class="text-subtitle-2 mr-2">Tier:</span>
                <v-chip-group v-model="selectedTier" class="mr-12">
                  <v-chip
                    v-for="(tier, index) in tiers"
                    :key="`tier_${index}`"
                    rounded
                    small
                    :class="tierChipClass(index)"
                  >
                    <span v-if="index == 0"> {{ $t('details.baseWheel') }}</span>
                    <span v-else>Tier {{ index + 1 }}</span>
                  </v-chip>
                </v-chip-group>
                <span class="text-subtitle-2 mr-2">{{ $t("details.segment_options") }}:</span>
                <v-chip-group
                  v-model="segmentChoice"
                  data-test="seg_num-chips"
                >
                  <v-chip
                    v-for="option in segment_options"
                    :key="`segment_option_${option}`"
                    :value="option"
                    :rules="(v) => !!v || 'No of segments is required'"
                    :input-value="segmentChoice == option"
                    :class="
                      option == segmentChoice
                        ? `white--text seg_${segmentChoice}`
                        : 'black--text outlined white'
                    "
                    mandatory
                    @change="triggerSegmentUpdateConfirmation(option)"
                  >
                    {{ option }}
                  </v-chip>
                </v-chip-group>
              </v-col>
            </v-row>

            <v-row>
              <v-divider class="mb-6"></v-divider>
            </v-row>
          </v-form>

          <v-form ref="segmentForm">
            <v-row>
              <v-col cols="12" @dragover="handleContentDragOver" @drop="handleContentDrop">
            <p class="mb-1 text-subtitle-2">{{ $t("details.upload_content")}}
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                </template>
                <span>{{ $t('details.upload_content_subtitle') }}</span>
              </v-tooltip>
            </p>            

            <v-file-input
              v-model="contentFiles"
              label="Drag and drop, or browse your files"
              accept="image/*, .mp4, .webm"
              counter
              multiple
              :disabled="disableField"
              outlined
              :show-size="true"
              prepend-icon=""
              class="file_upload"
              @change="onContentFilesUploaded"
            >
              <template v-slot:selection="{ text }">
                <v-chip dark label small class="primary">
                  {{ text }}
                </v-chip>
              </template>
            </v-file-input>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-progress-linear
              v-if="contentUploading"
              :value="contentUploadProgress"
              color="primary"
              height="5"
              class="ma-2"
            ></v-progress-linear>
            <p
              v-if="contentUploading"
              class="ml-3 text-subtitle-2 font-weight-light"
            >
              Uploading {{ contentUploadedCount }} of {{ contentFiles.length }}
            </p>
          </v-col>
        </v-row>

        <v-row class="ma-0">
          <v-col cols="12" class="pa-0">
            <v-expansion-panels class="mt-0" flat>
              <v-expansion-panel>
                <v-expansion-panel-header class="py-4 px-6" style="font-size: 16px; font-weight: 500;">
                  Background Content
                </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row class="mt-2">
                <v-col cols="4">
                  <p class="mb-2 text-subtitle-2">Content
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                      </template>
                      <span>{{ $t('details.select_background_content_subtitle') }}</span>
                    </v-tooltip>
                  </p>
                  <v-select
                    v-model="backgroundContent"
                    :items="backgroundOptions"
                    :disabled="disableField"
                    label="Make a selection"
                    :item-text="assetNameFormatted"
                    return-object
                    dense
                    flat
                    outlined
                    solo
                    clearable
                    searchable
                    :hint="backgroundContentHint"
                    persistent-hint
                  >
                    <template v-slot:prepend-item>
                      <v-text-field v-model="searchBackgroundTerm" placeholder="Search" @input="searchBackground" class="ml-2 mr-2 pt-0"></v-text-field>
                      <v-divider class=""></v-divider>
                    </template>
                    <template v-slot:item="{ item }">
                      {{ assetNameFormatted(item) }}
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="4">
                  <p class="mb-2 text-subtitle-2">Size
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                      </template>
                      <span>{{ $t('details.select_foreground_content_size_subtitle') }}</span>
                    </v-tooltip>
                  </p>
                  <v-text-field
                    v-model="backgroundContentSize"
                    placeholder="Enter a value"
                    :rules="[numberBetweenOneAndHundred]"
                    :disabled="disableField"
                    dense
                    solo
                    flat
                    outlined
                  ></v-text-field>
                </v-col>

                <v-col cols="4">
                  <p class="mb-2 text-subtitle-2">Rotation
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                      </template>
                      <span>{{ $t('details.select_background_rotation_subtitle') }}</span>
                    </v-tooltip>
                  </p>
                  <v-select
                    v-model="backgroundRotation"
                    :items="rotationOptions"
                    :disabled="disableField"
                    label="Make a selection"
                    dense
                    flat
                    outlined
                    solo
                  >
                  </v-select>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>

        <v-row class="ma-0">
          <v-col cols="12" class="pa-0">
            <v-expansion-panels class="" flat>
              <v-expansion-panel>
                <v-expansion-panel-header class="py-4 px-6" style="font-size: 16px; font-weight: 500;">
                  Background Overlay Content
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row class="mt-2">
                <v-col cols="4">
                  <p class="mb-2 text-subtitle-2">Content
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                      </template>
                      <span>{{ $t('details.select_backgroundOverlay_content_subtitle') }}</span>
                    </v-tooltip>
                  </p>
                  <v-select
                    v-model="backgroundOverlayContent"
                    :items="backgroundOverlayOptions"
                    :disabled="disableField"
                    label="Make a selection"
                    :item-text="assetNameFormatted"
                    return-object
                    dense
                    flat
                    outlined
                    solo
                    clearable
                    searchable
                    :hint="backgroundOverlayContentHint"
                    persistent-hint
                  >
                    <template v-slot:prepend-item>
                      <v-text-field v-model="searchBackgroundOverlayTerm" placeholder="Search" @input="searchBackgroundOverlay" class="ml-2 mr-2 pt-0"></v-text-field>
                      <v-divider class=""></v-divider>
                    </template>
                    <template v-slot:item="{ item }">
                      {{ assetNameFormatted(item) }}
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="4">
                  <p class="mb-2 text-subtitle-2">Size
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                      </template>
                      <span>{{ $t('details.select_foreground_content_size_subtitle') }}</span>
                    </v-tooltip>
                  </p>
                  <v-text-field
                    v-model="backgroundOverlayContentSize"
                    placeholder="Enter a value"
                    :rules="[numberBetweenOneAndHundred]"
                    :disabled="disableField"
                    dense
                    solo
                    flat
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="4">
                  <p class="mb-2 text-subtitle-2">Rotation
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                      </template>
                      <span>{{ $t('details.select_backgroundOverlay_rotation_subtitle') }}</span>
                    </v-tooltip>
                  </p>
                  <v-select
                    v-model="backgroundOverlayRotation"
                    :items="rotationOptions"
                    :disabled="disableField"
                    label="Make a selection"
                    dense
                    flat
                    outlined
                    solo
                  >
                  </v-select>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>

        <v-row class="ma-0">
          <v-col cols="12" class="pa-0">
            <v-expansion-panels class="" flat>
              <v-expansion-panel>
                <v-expansion-panel-header class="py-4 px-6" style="font-size: 16px; font-weight: 500;">
                  Wheel Overlay Content
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row class="mt-2">
                <v-col cols="4">
                  <p class="mb-2 text-subtitle-2">
                    Content
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                      </template>
                      <span>{{ $t('details.select_wheelOverlay_content_subtitle') }}</span>
                    </v-tooltip>
                  </p>
                  <v-select
                    v-model="wheelOverlayContent"
                    :items="wheelOverlayOptions"
                    :disabled="disableField"
                    label="Make a selection"
                    :item-text="assetNameFormatted"
                    item-value="displayName"
                    return-object
                    dense
                    flat
                    outlined
                    solo
                    clearable
                    searchable
                    :hint="wheelOverlayContentHint"
                    persistent-hint
                  >
                    <template v-slot:prepend-item>
                      <v-text-field v-model="searchWheelOverlayTerm" placeholder="Search" @input="searchWheelOverlay" class="ml-2 mr-2 pt-0"></v-text-field>
                      <v-divider class=""></v-divider>
                    </template>
                    <template v-slot:item="{ item }">
                      {{ assetNameFormatted(item) }}
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="4">
                  <p class="mb-2 text-subtitle-2">Size
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                      </template>
                      <span>{{ $t('details.select_wheelOverlay_content_size_subtitle') }}</span>
                    </v-tooltip>
                  </p>
                  <v-text-field
                    v-model="wheelOverlayContentSize"
                    placeholder="Enter a value"
                    :rules="[numberBetweenOneAndHundred]"
                    :disabled="disableField"
                    dense
                    required
                    solo
                    flat
                    outlined
                  ></v-text-field>
                </v-col>

                <v-col cols="4">
                  <p class="mb-2 text-subtitle-2">Rotation
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                      </template>
                      <span>{{ $t('details.select_wheelOverlay_rotation_subtitle') }}</span>
                    </v-tooltip>
                  </p>
                  <v-select
                    v-model="wheelOverlayRotation"
                    :items="rotationOptions"
                    :disabled="disableField"
                    label="Make a selection"
                    dense
                    flat
                    outlined
                    solo
                  >
                  </v-select>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>

        <v-row class="ma-0">
          <v-col cols="12" class="pa-0">
            <v-expansion-panels class="" flat>
              <v-expansion-panel>
                <v-expansion-panel-header class="py-4 px-6" style="font-size: 16px; font-weight: 500;">
                  Foreground Content
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row class="mt-2">
                <v-col cols="4">
                  <p class="mb-2 text-subtitle-2">
                    Content
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                      </template>
                      <span>{{ $t('details.select_foreground_content_subtitle') }}</span>
                    </v-tooltip>
                  </p>
                  <v-select
                    v-model="foregroundContent"
                    :items="foregroundOptions"
                    :disabled="disableField"
                    label="Make a selection"
                    :item-text="assetNameFormatted"
                    item-value="displayName"
                    return-object
                    dense
                    flat
                    outlined
                    solo
                    clearable
                    searchable
                    :hint="foregroundContentHint"
                    persistent-hint
                  >
                    <template v-slot:prepend-item>
                      <v-text-field v-model="searchForegroundTerm" placeholder="Search" @input="searchForeground" class="ml-2 mr-2 pt-0"></v-text-field>
                      <v-divider class=""></v-divider>
                    </template>
                    <template v-slot:item="{ item }">
                      {{ assetNameFormatted(item) }}
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="4">
                  <p class="mb-2 text-subtitle-2">Size
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                      </template>
                      <span>{{ $t('details.select_foreground_content_size_subtitle') }}</span>
                    </v-tooltip>
                  </p>
                  <v-text-field
                    v-model="foregroundContentSize"
                    placeholder="Enter a value"
                    :rules="[numberBetweenOneAndHundred]"
                    :disabled="disableField"
                    dense
                    required
                    solo
                    flat
                    outlined
                  ></v-text-field>
                </v-col>

                <v-col cols="4">
                  <p class="mb-2 text-subtitle-2">Rotation
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                      </template>
                      <span>{{ $t('details.select_foreground_rotation_subtitle') }}</span>
                    </v-tooltip>
                  </p>
                  <v-select
                    v-model="foregroundRotation"
                    :items="rotationOptions"
                    :disabled="disableField"
                    label="Make a selection"
                    dense
                    flat
                    outlined
                    solo
                  >
                  </v-select>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>

        <v-row>
          <v-divider class="mt-4 mb-4"></v-divider>
        </v-row>

        <v-row>
          <v-col cols="12" md="6" class="pr-2"
            @dragover="handleSegmentContentDragOver"
            @drop="handleSegmentContentDrop">
            <p class="mb-1 text-subtitle-2">{{ $t("details.upload_segment_content")}}
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                </template>
                <span>{{ $t('details.upload_segment_subtitle') }}</span>
              </v-tooltip>
            </p>

            <v-file-input
              v-model="segmentFiles"
              label="Drag and drop, or browse your files"
              accept="image/*"
              counter
              :disabled="disableField"
              multiple
              outlined
              :show-size="true"
              prepend-icon=""
              class="file_upload"
              @change="onSegmentContentUploaded"
            >
              <template v-slot:selection="{ text }">
                <v-chip dark label small class="primary">
                  {{ text }}
                </v-chip>
              </template>
            </v-file-input>
          </v-col>
          <v-col cols="12" md="6" class="pl-2"
            @dragover="handleSegmentAnimationDragOver"
            @drop="handleSegmentAnimationDrop">
            <p class="mb-1 text-subtitle-2">{{ $t("details.upload_segment_animations")}}
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                </template>
                <span>{{ $t('details.upload_segment_animations_subtitle') }}</span>
              </v-tooltip>
            </p>

            <v-file-input
              v-model="segmentAnimationFiles"
              label="Drag and drop, or browse your files"
              accept=".mp4, .webm"
              counter
              :disabled="disableField"
              multiple
              outlined
              :show-size="true"
              prepend-icon=""
              class="file_upload"
              @change="onSegmentAnimationsUploaded"
            >
              <template v-slot:selection="{ text }">
                <v-chip dark label small class="primary">
                  {{ text }}
                </v-chip>
              </template>
            </v-file-input>
          </v-col>
        </v-row>
        <v-row v-if="segmentUploading || segmentAnimationUploading">
          <v-col cols="12">
            <v-progress-linear
              v-if="segmentUploading"
              :value="segmentUploadProgress"
              color="primary"
              height="5"
              class="mb-2"
            ></v-progress-linear>
            <p
              v-if="segmentUploading"
              class="text-subtitle-2 font-weight-light mb-3"
            >
              Uploading segment images: {{ segmentUploadedCount }} of {{ segmentFiles.length }}
            </p>
            <v-progress-linear
              v-if="segmentAnimationUploading"
              :value="segmentAnimationUploadProgress"
              color="primary"
              height="5"
              class="mb-2"
            ></v-progress-linear>
            <p
              v-if="segmentAnimationUploading"
              class="text-subtitle-2 font-weight-light"
            >
              Uploading animations: {{ segmentAnimationUploadedCount }} of {{ segmentAnimationFiles.length }}
            </p>
          </v-col>
        </v-row>
        <v-row class="ma-0 mt-2 mb-6">
          <v-col cols="12" class="pa-0">
            <v-expansion-panels class="" flat>
              <v-expansion-panel>
                <v-expansion-panel-header class="py-4 px-6" style="font-size: 16px; font-weight: 500;">
                  Winning Animation Settings
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row class="mt-2">
                    <v-col cols="4">
                      <p class="mb-2 text-subtitle-2">Size
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                          </template>
                          <span>{{ $t('details.segment_animation_size_subtitle') }}</span>
                        </v-tooltip>
                      </p>
                      <v-text-field
                        v-model="winningAnimationSize"
                        label="Enter a value"
                        :rules="[numberBetweenOneAndHundred]"
                        :disabled="disableField"
                        dense
                        flat
                        outlined
                        solo
                      ></v-text-field>
                    </v-col>
                    <v-col cols="4">
                      <p class="mb-2 text-subtitle-2">Duration
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                          </template>
                          <span>{{ $t('details.segment_animation_duration_subtitle') }}</span>
                        </v-tooltip>
                      </p>
                      <v-text-field
                        v-model="winningAnimationDuration"
                        label="Enter a value"
                        :rules="[numberGreaterThanZero]"
                        :disabled="disableField"
                        dense
                        flat
                        outlined
                        solo
                      ></v-text-field>
                    </v-col>
                    <v-col cols="4">
                      <p class="mb-2 text-subtitle-2">Delay
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                          </template>
                          <span>{{ $t('details.segment_animation_delay_subtitle') }}</span>
                        </v-tooltip>
                      </p>
                      <v-text-field
                        v-model="winningAnimationDelay"
                        label="Enter a value"
                        :rules="[numberGreaterThanZero]"
                        :disabled="disableField"
                        dense
                        flat
                        outlined
                        solo
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>

        <v-row>
          <v-divider></v-divider>
        </v-row>

        <v-row>
          <v-col cols="12" class="mr-3">
            <p class="mb-2 text-subtitle-2">Segment Selection</p>
            <v-chip-group
              v-model="selectedSegmentDefinition"
              :disabled="disableField"
            >
              <v-chip
                v-for="(seg, index) in segmentDefinitions"
                :key="`segment_definition_${index}`"
                :disabled="disableField"
                rounded
                small
                mandatory
                active-class="primary"
                @change="selectSegmentDefinition(index)"
                :value="index"
              >
                {{ seg.name ? seg.name : index + 1 }}
              </v-chip>
              <v-btn
                elevation="0"
                small
                rounded
                class="mr-2 red white--text float-right"
                @click="deleteSegmentDialog = true"
                :disabled="disableField"
                data-test="add_seg-btn"
                ><v-icon>mdi-minus</v-icon></v-btn
              >
              <v-btn
                elevation="0"
                small
                rounded
                class="black white--text float-right"
                @click="addSegment"
                :disabled="disableField"
                data-test="add_seg-btn"
                ><v-icon>mdi-plus</v-icon></v-btn
              >
            </v-chip-group>
          </v-col>
        </v-row>

        <v-row class="mt-4">
          <v-col cols="12" sm="6" md="4">
            <p class="mb-2 text-subtitle-2"><span>Segment Value</span>
            </p>
            <v-text-field
              v-model="segmentName"
              placeholder="Segment name or value"
              :rules="segmentNameRules"
              :disabled="disableField"
              dense
              solo
              flat
              outlined
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <p class="mb-2 text-subtitle-2">
              Value Type
            </p>
            <v-select
              v-model="segmentValueType"
              :items="segmentValueTypeOptions"
              :disabled="isSegmentValueTypeDropdownDisabled"
              return-object
              item-text="name"
              :label="segmentValueType ? segmentValueTypeOptions.length > 0 ? 'Make a selection' : 'N/A' : 'Make a selection'"
              dense
              flat
              outlined
              solo
            ></v-select>
          </v-col>

          <v-col cols="12" sm="6" md="3" v-if="showCustomTextField">
            <p class="mb-2 text-subtitle-2">
              &nbsp; <!--empty tag to match spacing exactly -->
            </p>
            <v-btn
              class="primary text-none"
              @click="addTypeDialog = true"
            >
              Custom Value Type
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <p class="mb-2 text-subtitle-2">
              Bonus Segment
            </p>
            <v-btn :style="disableBonusSwitch" id="yesBonusButton" dense @click="setBonusSegment(true)" :class="yesBonusButtonClass" class="px-6 text-none rounded-lg">
                Yes
            </v-btn>
            <v-btn :style="disableBonusSwitch" id="noBonusButton" dense @click="setBonusSegment(false)" :class="noBonusButtonClass" class="px-6 text-none rounded-lg">
                No
            </v-btn>
          </v-col>
        </v-row>

        <v-row class="mt-0">
          <v-col cols="12" sm="6" md="4">
            <p class="mb-2 text-subtitle-2">
              Image
            </p>
            <v-select
              v-model="segmentImage"
              :items="segmentImageOptions"
              label="Make a selection"
              :disabled="disableField"
              :item-text="assetNameFormatted"
              return-object
              dense
              solo
              flat
              outlined
              @change="selectSegmentImage"
              clearable
              searchable
              :hint="segmentImageHint"
              persistent-hint
            >
              <template v-slot:prepend-item>
                <v-text-field v-model="searchSegmentImageTerm" placeholder="Search" @input="searchSegmentImage" class="ml-2 mr-2 pt-0"></v-text-field>
                <v-divider class=""></v-divider>
              </template>
              <template v-slot:item="{ item }">
                {{ assetNameFormatted(item) }}
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <p class="mb-2 text-subtitle-2">
              Winning Animation
            </p>
            <v-select
              v-model="segmentAnimation"
              :items="segmentAnimationOptions"
              label="Make a selection"
              :disabled="disableField"
              :item-text="assetNameFormatted"
              return-object
              dense
              flat
              solo
              outlined
              :hint="segmentAnimationHint"
              persistent-hint
              @change="selectSegmentAnimation"
              clearable
              searchable
            >
              <template v-slot:prepend-item>
                <v-text-field v-model="searchSegmentAnimationTerm" placeholder="Search" @input="searchSegmentAnimation" class="ml-2 mr-2 pt-0"></v-text-field>
                <v-divider class=""></v-divider>
              </template>
              <template v-slot:item="{ item }">
                {{ assetNameFormatted(item) }}
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <p class="mb-2 text-subtitle-2">
              <span>Groups</span>
              <v-tooltip top content-class="segment_value_tooltip_width">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small class="ml-1">mdi-help-circle</v-icon>
                </template>
                <span>{{ $t('details.segment_groups_subtitle') }}</span>
              </v-tooltip>
            </p>
            <v-text-field
              v-model="segmentGroup"
              placeholder="Groups"
              :rules="segmentGroupRules"
              :disabled="disableField"
              dense
              solo
              flat
              outlined
              :key="selectedSegmentDefinition"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row style="height: 180px;">
          <v-col>
            <!-- Empty column to take up space -->
          </v-col>
        </v-row>
          </v-form>

          <v-dialog v-model="addTypeDialog" max-width="500px">
            <v-card>
              <v-card-title class="headline">Add Segment Value Type</v-card-title>
              <v-card-text>
                <div>Enter the name and value of the segment value type</div>
                <div>
                  <v-text-field v-model="customSegmentName" label="Name" :hint="`This name appears on the kiosk e.g. 'You Won $100 ${customSegmentName}'`"></v-text-field>
                  <v-text-field v-model="customSegmentValue" label="Value" hint="This value should match your CMS (e.g. bucket name, account type, etc.)"></v-text-field>
                </div>
              </v-card-text>
              <v-card-actions>
                <v-btn color="primary" text @click="addSegmentType" :disabled="!(!!customSegmentName && !!customSegmentValue)">Save</v-btn>
                <v-btn color="red" text @click="addTypeDialog = false">Cancel</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Confirmation Dialogs -->
          <v-dialog v-model="warningDialog" max-width="500px">
            <v-card>
              <v-card-title class="headline">Are you sure?</v-card-title>
              <v-card-text> Are you sure you want to proceed? </v-card-text>
              <v-card-actions>
                <v-btn color="primary" text @click="updateSegmentCount">Yes</v-btn>
                <v-btn color="red" text @click="cancelSegmentUpdate">No</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-dialog v-model="deleteSegmentDialog" max-width="500px">
            <v-card>
              <v-card-title class="headline">Delete Segment?</v-card-title>
              <v-card-text>  This will remove all instances of the segment in the build wheel step. </v-card-text>
              <v-card-actions>
                <v-btn color="primary" text @click="deleteSelectedSegmentDefinition">Yes</v-btn>
                <v-btn color="red" text @click="deleteSegmentDialog = false">No</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
        <v-spacer></v-spacer>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import _ from "lodash";

export default {
  data: () => ({
    valid: false,

    fileInputAttrs: {
        multiple: true, // Allow multiple file selection
      },
    segment_options: [54, 27, 18, 9, 3],
    addTypeDialog: false,
    warningDialog: false,
    segmentToUpdate: null,
    rotationOptions: ["None", "Reverse", "Double Speed", "Counter"],
    selectedSegmentDefinition: 0,

    fileRules: [
      (value) =>
        !value || value.size < 10000000 || "File size should not exceed 10 MB!", // TODO - specify correct size limit
    ],
    segmentNameRules: [
      v => !!v || 'Value is required',
      v => /^[A-Za-z0-9\s_\-@#!.'"()[\]]*$/.test(v) || "Segment value invalid",
    ],
    segmentGroupRules: [
      // eslint-disable-next-line no-useless-escape
      v =>   /^[A-Za-z0-9\s_\-@#!.'"\$€£¥]+(,[A-Za-z0-9\s_\-@#!.'"\$€£¥]+)*(?<!\s)$/.test(v) || "Value must be a comma-separated list of valid characters."
    ],
    contentFiles: [],
    contentUploading: false,
    contentUploadProgress: 0,
    contentUploadedCount: 0,

    segmentFiles: [],
    segmentAnimationFiles: [],
    segmentItems: [],
    segmentUploading: false,
    segmentUploadProgress: 0,
    segmentUploadedCount: 0,
    segmentAnimationUploading: false,
    segmentAnimationUploadProgress: 0,
    segmentAnimationUploadedCount: 0,
    segmentBonusCooldown: false,

    searchForegroundTerm: '',
    foregroundOptions: [],
    foregroundOptionsCopy: [],
    searchBackgroundTerm: '',
    backgroundOptions: [],
    backgroundOptionsCopy: [],
    
    searchWheelOverlayTerm: '',
    wheelOverlayOptions: [],
    wheelOverlayOptionsCopy: [],
    searchBackgroundOverlayTerm: '',
    backgroundOverlayOptions: [],
    backgroundOverlayOptionsCopy: [],

    searchSegmentImageTerm: '',
    segmentImageOptions: [],
    segmentImageOptionsCopy: [],
    searchSegmentAnimationTerm: '',
    segmentAnimationOptions: [],
    segmentAnimationOptionsCopy: [],
    deleteSegmentDialog: false,

    // Collapsible sections
    backgroundExpanded: false,
    backgroundOverlayExpanded: false,
    wheelOverlayExpanded: false,
    foregroundExpanded: false,

    customSegmentName: '',
    customSegmentValue: ''

  }),
  async created() {
    
  },
  async mounted(){
   // if(!this.getIntegrationValues){
      this.selectTenant(this.getTenant);
   // }
    this.selectedTier = 0;
    
    // Set default segment choice to 54 if not already set
    if (this.getSelectedGame.no_of_segments == null || this.getSelectedGame.no_of_segments == 0) {
      await this.updateSegmentChoice({
        noOfSegments: 54,
        tierIndex: 0,
      });
      await this.getContentByPrefixAndTenant({
        prefix: `${this.getTenant}/segment/54`, 
        tenantId: this.getTenant, 
        update: "updateSegmentOptions"
      });
      this.segmentImageOptions = [...this.getSegmentOptions].reverse();
      this.segmentImageOptionsCopy = this.segmentImageOptions;
    }
    
    this.setOptionsDropdowns();
  },
  computed: {
    ...mapGetters([ 
      "getSelectedGame",
      "getCurrentStage",
      "getContentOptions",
      "getSegmentOptions",
      "getWinningAnimationsOptions",
      "getTenant",
      "getUploadContentError",
      "getDropdownsLoaded",
      "getSelectedIntegrationName",
      "getIntegrationValueTypes"
    ]),
    showCustomTextField(){
      return this.getIntegrationValueTypes && this.getIntegrationValueTypes.find(it => it.value == 'custom')
    },
    disableBonusSwitch() {
      if (this.selectedTier == 0) {
        return this.getSelectedGame.tiers.length == 0 || this.getSelectedGame.no_of_segments == null ? "pointer-events: none; opacity: 0.5 !important;" : "";
      } else {
        return this.getSelectedGame.tiers[this.selectedTier - 1].no_of_segments == null
         || this.getSelectedGame.tiers[this.selectedTier - 1].no_of_segments == 0 
         || this.selectedTier == this.getSelectedGame.tiers.length
            ? "pointer-events: none; opacity: 0.5 !important;" : "";
      } 
    },
    yesBonusButtonClass() {
      if (this.selectedTier == 0) {
        return this.selectedSegmentDefinition != null 
          && this.getSelectedGame.segment_definitions[this.selectedSegmentDefinition].bonusSegment
          ? 'yes_button_bonus' : 'yes_button_nobonus';
      } else {
        return this.selectedSegmentDefinition != null 
          && this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions[this.selectedSegmentDefinition].bonusSegment 
          ? 'yes_button_bonus' : 'yes_button_nobonus';
      } 
    },
    noBonusButtonClass() {
      if (this.selectedTier == 0) {
        return this.selectedSegmentDefinition != null 
          && !this.getSelectedGame.segment_definitions[this.selectedSegmentDefinition].bonusSegment 
          ?  'no_button_nobonus' : 'no_button_bonus';
      } else {
        return this.selectedSegmentDefinition != null 
          && !this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions[this.selectedSegmentDefinition].bonusSegment 
          ?  'no_button_nobonus' : 'no_button_bonus';
      }
    },
    tiers() {
      return this.getSelectedGame.tiers.length + 1;
    },
    selectedTier: {
      get() {
        return this.getSelectedGame.selectedTier;
      },
      set(index) {
        if (index == undefined) return;
        this.selectedSegmentDefinition = 0;
        this.updateSelectedTier(index);
        const noOfSegments = index == 0 ? this.getSelectedGame.no_of_segments : this.getSelectedGame.tiers[index - 1].no_of_segments;
        
        this.changeSelectedTier(noOfSegments);
      },
    },
    segmentChoice: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.no_of_segments;
        }
        return this.getSelectedGame.tiers[this.selectedTier - 1] ? this.getSelectedGame.tiers[this.selectedTier - 1].no_of_segments : null;
      },
      set() {
        // First choice should be set here but updates must go through dialog confirmation first.
      },
    },
    disableField() {
      return this.segmentChoice == null || this.segmentChoice == 0;
    },
    tierOptions() {
      return this.getTierOptions;
    },
    foregroundContent: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.foreground &&
            this.getSelectedGame.foreground.asset
            ? this.getSelectedGame.foreground.asset
            : null;
        }
        return this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].foreground &&
          this.getSelectedGame.tiers[this.selectedTier - 1].foreground.asset
          ? this.getSelectedGame.tiers[this.selectedTier - 1].foreground.asset
              
          : null;
      },
      set(foregroundContent) {
        this.updateForegroundContent({
          tier: this.selectedTier,
          index: this.selectedSegmentDefinition,
          foregroundContent: foregroundContent,
        });
      },
    },
    foregroundContentSize: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.foreground
            ? this.getSelectedGame.foreground.assetSize
            : null;
        }
        return this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].foreground
          ? this.getSelectedGame.tiers[this.selectedTier - 1].foreground
              .assetSize
          : null;
      },
      set: _.debounce(function (assetSize) {
        if (assetSize == null || assetSize == '') assetSize = 50;
        if (!(/^[0-9]*$/.test(assetSize))) return;
        this.updateForegroundContentSize({
          tier: this.selectedTier,
          index: this.selectedSegmentDefinition,
          assetSize: assetSize,
        });
      }, 500),
    },
    foregroundRotation: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.foreground &&
            this.getSelectedGame.foreground.rotation
            ? this.getSelectedGame.foreground.rotation
            : null;
        }
        return this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].foreground &&
          this.getSelectedGame.tiers[this.selectedTier - 1].foreground.rotation
          ? this.getSelectedGame.tiers[this.selectedTier - 1].foreground.rotation
              
          : null;
      },
      set(rotation) {
        this.updateForegroundRotation({
          tier: this.selectedTier,
          rotation: rotation,
        });
      },
    },
    backgroundContent: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.background &&
            this.getSelectedGame.background.asset
            ? this.getSelectedGame.background.asset
            : null;
        }
        return this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].background &&
          this.getSelectedGame.tiers[this.selectedTier - 1].background.asset
          ? this.getSelectedGame.tiers[this.selectedTier - 1].background.asset
              
          : null;
      },
      set(backgroundContent) {
        this.updateBackgroundContent({
          tier: this.selectedTier,
          backgroundContent: backgroundContent,
        });
      },
    },
    backgroundContentSize: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.background
            ? this.getSelectedGame.background.assetSize
            : null;
        }
        return this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].background
          ? this.getSelectedGame.tiers[this.selectedTier - 1].background
              .assetSize
          : null;
      },
      set: _.debounce(function (assetSize) {
        if (assetSize == null || assetSize == '') assetSize = 50;
        if (!(/^[0-9]*$/.test(assetSize))) return;
        this.updateBackgroundContentSize({
          tier: this.selectedTier,
          index: this.selectedSegmentDefinition,
          assetSize: assetSize,
        });
      }, 500),
    },
    backgroundRotation: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.background &&
            this.getSelectedGame.background.rotation
            ? this.getSelectedGame.background.rotation
            : null;
        }
        return this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].background &&
          this.getSelectedGame.tiers[this.selectedTier - 1].background.rotation
          ? this.getSelectedGame.tiers[this.selectedTier - 1].background.rotation
              
          : null;
      },
      set(rotation) {
        this.updateBackgroundRotation({
          tier: this.selectedTier,
          rotation: rotation,
        });
      },
    },
    wheelOverlayContent: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.wheelOverlay &&
            this.getSelectedGame.wheelOverlay.asset
            ? this.getSelectedGame.wheelOverlay.asset
            : null;
        }
        return this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].wheelOverlay &&
          this.getSelectedGame.tiers[this.selectedTier - 1].wheelOverlay.asset
          ? this.getSelectedGame.tiers[this.selectedTier - 1].wheelOverlay.asset
              
          : null;
      },
      set(wheelOverlayContent) {
        this.updateWheelOverlayContent({
          tier: this.selectedTier,
          index: this.selectedSegmentDefinition,
          wheelOverlayContent: wheelOverlayContent,
        });
      },
    },
    wheelOverlayContentSize: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.wheelOverlay
            ? this.getSelectedGame.wheelOverlay.assetSize
            : null;
        }
        return this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].wheelOverlay
          ? this.getSelectedGame.tiers[this.selectedTier - 1].wheelOverlay
              .assetSize
          : null;
      },
      set: _.debounce(function (assetSize) {
        if (assetSize == null || assetSize == '') assetSize = 50;
        if (!(/^[0-9]*$/.test(assetSize))) return;
        this.updateWheelOverlayContentSize({
          tier: this.selectedTier,
          index: this.selectedSegmentDefinition,
          assetSize: assetSize,
        });
      }, 500),
    },
    wheelOverlayRotation: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.wheelOverlay &&
            this.getSelectedGame.wheelOverlay.rotation
            ? this.getSelectedGame.wheelOverlay.rotation
            : null;
        }
        return this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].wheelOverlay &&
          this.getSelectedGame.tiers[this.selectedTier - 1].wheelOverlay.rotation
          ? this.getSelectedGame.tiers[this.selectedTier - 1].wheelOverlay.rotation
              
          : null;
      },
      set(rotation) {
        this.updateWheelOverlayRotation({
          tier: this.selectedTier,
          rotation: rotation,
        });
      },
    },
    backgroundOverlayContent: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.backgroundOverlay &&
            this.getSelectedGame.backgroundOverlay.asset
            ? this.getSelectedGame.backgroundOverlay.asset
            : null;
        }
        return this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].backgroundOverlay &&
          this.getSelectedGame.tiers[this.selectedTier - 1].backgroundOverlay.asset
          ? this.getSelectedGame.tiers[this.selectedTier - 1].backgroundOverlay.asset
              
          : null;
      },
      set(backgroundOverlayContent) {
        this.updateBackgroundOverlayContent({
          tier: this.selectedTier,
          index: this.selectedSegmentDefinition,
          backgroundOverlayContent: backgroundOverlayContent,
        });
      },
    },
    backgroundOverlayContentSize: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.backgroundOverlay
            ? this.getSelectedGame.backgroundOverlay.assetSize
            : null;
        }
        return this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].backgroundOverlay
          ? this.getSelectedGame.tiers[this.selectedTier - 1].backgroundOverlay
              .assetSize
          : null;
      },
      set: _.debounce(function (assetSize) {
        if (assetSize == null || assetSize == '') assetSize = 50;
        if (!(/^[0-9]*$/.test(assetSize))) return;
        this.updateBackgroundOverlayContentSize({
          tier: this.selectedTier,
          index: this.selectedSegmentDefinition,
          assetSize: assetSize,
        });
      }, 500),
    },
    backgroundOverlayRotation: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.backgroundOverlay &&
            this.getSelectedGame.backgroundOverlay.rotation
            ? this.getSelectedGame.backgroundOverlay.rotation
            : null;
        }
        return this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].backgroundOverlay &&
          this.getSelectedGame.tiers[this.selectedTier - 1].backgroundOverlay.rotation
          ? this.getSelectedGame.tiers[this.selectedTier - 1].backgroundOverlay.rotation
              
          : null;
      },
      set(rotation) {
        this.updateBackgroundOverlayRotation({
          tier: this.selectedTier,
          rotation: rotation,
        });
      },
    },
    winningAnimationSize: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.animationSize
            ? this.getSelectedGame.animationSize
            : null;
        }
        return this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].animationSize
          ? this.getSelectedGame.tiers[this.selectedTier - 1].animationSize
          : null;
      },
      set: _.debounce(function (assetSize) {
        if (assetSize == null || assetSize == '') assetSize = 100;
        if (!(/^[0-9]*$/.test(assetSize))) return;
        this.updateWinningAnimationSize({
          tier: this.selectedTier,
          assetSize: assetSize,
        });
      }, 500),
    },
    winningAnimationDuration: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.animation && this.getSelectedGame.animation.winningAnimationDuration
            ? this.getSelectedGame.animation.winningAnimationDuration
            : null;
        }
        return this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].animation &&
          this.getSelectedGame.tiers[this.selectedTier - 1].animation.winningAnimationDuration
          ? this.getSelectedGame.tiers[this.selectedTier - 1].animation.winningAnimationDuration
          : null;
      },
      set: _.debounce(function (winningAnimationDuration) {
        if (winningAnimationDuration == null || winningAnimationDuration == '') winningAnimationDuration = 100;
        if (!(/^[0-9]*$/.test(winningAnimationDuration))) return;
        this.updateWinningAnimationDuration({
          tier: this.selectedTier,
          winningAnimationDuration: winningAnimationDuration,
        });
      }, 500),
    },
    winningAnimationDelay: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.animation && this.getSelectedGame.animation.winningAnimationDelay
            ? this.getSelectedGame.animation.winningAnimationDelay
            : null;
        }
        return this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].animation &&
          this.getSelectedGame.tiers[this.selectedTier - 1].animation.winningAnimationDelay
          ? this.getSelectedGame.tiers[this.selectedTier - 1].animation.winningAnimationDelay
          : null;
      },
      set: _.debounce(function (winningAnimationDelay) {
        if (winningAnimationDelay == null || winningAnimationDelay == '') winningAnimationDelay = 100;
        if (!(/^[0-9]*$/.test(winningAnimationDelay))) return;
        this.updateWinningAnimationDelay({
          tier: this.selectedTier,
          winningAnimationDelay: winningAnimationDelay,
        });
      }, 500),
    },
    segments() {
      return this.selectedTier == 0
          ? this.getSelectedGame.segments 
        : this.getSelectedGame.tiers[this.selectedTier - 1].segments;
    },
    segmentDefinition() {
      if (!this.selectedSegmentDefinition >= 0 || !this.selectedTier >= 0) {
        return null;
      }
      if (this.selectedTier == 0) {
        return this.getSelectedGame.segment_definitions
          ? this.getSelectedGame.segment_definitions[
              this.selectedSegmentDefinition
            ]
          : null;
      }
      return this.getSelectedGame.tiers &&
        this.getSelectedGame.tiers[this.selectedTier - 1] &&
        this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions
          .length >= 0
        ? this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions[
            this.selectedSegmentDefinition
          ]
        : null;
    },
    segmentDefinitions() {
      if (this.selectedTier == 0) {
        return this.getSelectedGame.segment_definitions || [];
      }
      return this.getSelectedGame.tiers &&
        this.getSelectedGame.tiers[this.selectedTier - 1]
        ? this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions
        : [];
    },
    segmentDefinitionCount() {
      if (!this.selectedTier >= 0) {
        return null;
      }
      if (this.selectedTier == 0) {
        return this.getSelectedGame.segment_definitions
          ? this.getSelectedGame.segment_definitions.length
          : null;
      }
      return this.getSelectedGame.tiers &&
        this.getSelectedGame.tiers[this.selectedTier - 1] &&
        this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions
        ? this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions
            .length
        : null;
    },
    segmentName: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.segment_definitions &&
            this.getSelectedGame.segment_definitions[
              this.selectedSegmentDefinition
            ]
            ? this.getSelectedGame.segment_definitions[
                this.selectedSegmentDefinition
              ].name
            : null;
        }
        return this.getSelectedGame.tiers &&
          this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1]
            .segment_definitions &&
          this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions[
            this.selectedSegmentDefinition
          ]
          ? this.getSelectedGame.tiers[this.selectedTier - 1]
              .segment_definitions[this.selectedSegmentDefinition].name
          : null;
      },
      set: _.debounce(function (name) {
        this.updateSegmentDefinitionName({
          name: name.trim(),
          index: this.selectedSegmentDefinition,
          tier: this.selectedTier,
        });
      }, 500),
    },
    segmentGroup: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.segment_definitions &&
            this.getSelectedGame.segment_definitions[
              this.selectedSegmentDefinition
            ] &&
            this.getSelectedGame.segment_definitions[
              this.selectedSegmentDefinition
            ].group
            ? this.getSelectedGame.segment_definitions[
                this.selectedSegmentDefinition
              ].group
            : null;
        }
        return this.getSelectedGame.tiers &&
          this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1]
            .segment_definitions &&
          this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions[
            this.selectedSegmentDefinition
          ]
          ? this.getSelectedGame.tiers[this.selectedTier - 1]
              .segment_definitions[this.selectedSegmentDefinition].group
          : null;
      },
      set: _.debounce(function (group) {
        group = group.split(',')
          .map(item => item.trim())
          .filter(Boolean)
          .join(', ');
        this.updateSegmentDefinitionGroup({
          group: group.trim(),
          index: this.selectedSegmentDefinition,
          tier: this.selectedTier,
        });
      }, 500),
    },
    segmentValueType: {
      get() {
        let currentValue = null;
        
        if (this.selectedTier == 0) {
          currentValue = this.getSelectedGame.segment_definitions &&
            this.getSelectedGame.segment_definitions[
              this.selectedSegmentDefinition
            ]
            ? this.getSelectedGame.segment_definitions[
                this.selectedSegmentDefinition
              ].segmentValueType
            : null;
        } else {
          currentValue = this.getSelectedGame.tiers &&
            this.getSelectedGame.tiers[this.selectedTier - 1] &&
            this.getSelectedGame.tiers[this.selectedTier - 1]
              .segment_definitions &&
            this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions[
              this.selectedSegmentDefinition
            ]
            ? this.getSelectedGame.tiers[this.selectedTier - 1]
                .segment_definitions[this.selectedSegmentDefinition].segmentValueType
            : null;
        }
        
        // If we have integrationValueTypes, find the key for the current value
        if (currentValue && this.getIntegrationValueTypes) {
          for (let i=0; i<this.getIntegrationValueTypes.length; i++) {
            if (this.getIntegrationValueTypes[i].value === currentValue.value) {
              return this.getIntegrationValueTypes[i];
            }
          }
        }
        
        return currentValue;
      },
      set: _.debounce(function (selectedKey) {
        // Get the value from integrationValueTypes map using the selected key
        let segmentValueType = this.getIntegrationValueTypes && this.getIntegrationValueTypes[selectedKey] 
          ? this.getIntegrationValueTypes[selectedKey] 
          : selectedKey;
        
        this.updateSegmentDefinitionValueType({
          segmentValueType: segmentValueType,
          index: this.selectedSegmentDefinition,
          tier: this.selectedTier,
        });
      }, 500),
    },
    segmentImage: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.segment_definitions &&
            this.getSelectedGame.segment_definitions[
              this.selectedSegmentDefinition
            ] &&
            this.getSelectedGame.segment_definitions[
              this.selectedSegmentDefinition
            ].image 
            ? this.getSelectedGame.segment_definitions[
                this.selectedSegmentDefinition
              ].image
            : null;
        }
        return this.getSelectedGame.tiers &&
          this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1]
            .segment_definitions &&
          this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions[
            this.selectedSegmentDefinition
          ] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions[
            this.selectedSegmentDefinition
          ].image
          ? this.getSelectedGame.tiers[this.selectedTier - 1]
              .segment_definitions[this.selectedSegmentDefinition].image
          : null;
      },
      set() {},
    },
    segmentAnimation: {
      get() {
        if (this.selectedTier == 0) {
          return this.getSelectedGame.segment_definitions &&
            this.getSelectedGame.segment_definitions[this.selectedSegmentDefinition] &&
            this.getSelectedGame.segment_definitions[this.selectedSegmentDefinition].animation
            ? this.getSelectedGame.segment_definitions[this.selectedSegmentDefinition].animation
            : null;
        }
        return this.getSelectedGame.tiers &&
          this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions &&
          this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions[this.selectedSegmentDefinition] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions[this.selectedSegmentDefinition].animation
          ? this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions[this.selectedSegmentDefinition].animation
          : null;
      },
      set() {
        // updated via @change function
      },
    },
    segmentBonus: {
      get() {
        if (this.selectedTier == 0) {
          let bonusSegment = this.getSelectedGame.segment_definitions &&
            this.getSelectedGame.segment_definitions[this.selectedSegmentDefinition]
            ? this.getSelectedGame.segment_definitions[this.selectedSegmentDefinition].bonusSegment
            : null;
          return bonusSegment == true ? 0 : 1;
        }
        return this.getSelectedGame.tiers &&
          this.getSelectedGame.tiers[this.selectedTier - 1] &&
          this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions &&
          this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions[this.selectedSegmentDefinition]
          ? this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions[this.selectedSegmentDefinition].bonusSegment
          : null;
      },
      set(bonusSegment) {
        if (this.getSelectedGame.tiers.length == 0) {
          return;
        }
        bonusSegment = bonusSegment == 0 ? true : false;
        this.updateSegmentDefinitionBonus({
          bonusSegment: bonusSegment,
          index: this.selectedSegmentDefinition,
          tier: this.selectedTier,
        });
      },
    },
    // For dropdown helper text if asset name overflows
    backgroundContentHint() {
      if (this.backgroundContent && this.backgroundContent.displayName) {
        const fullName = this.backgroundContent.displayName.substring(0, this.backgroundContent.displayName.lastIndexOf('.'));
        return fullName.length > 20 ? fullName : '';
      }
      return '';
    },
    backgroundOverlayContentHint() {
      if (this.backgroundOverlayContent && this.backgroundOverlayContent.displayName) {
        const fullName = this.backgroundOverlayContent.displayName.substring(0, this.backgroundOverlayContent.displayName.lastIndexOf('.'));
        return fullName.length > 20 ? fullName : '';
      }
      return '';
    },
    wheelOverlayContentHint() {
      if (this.wheelOverlayContent && this.wheelOverlayContent.displayName) {
        const fullName = this.wheelOverlayContent.displayName.substring(0, this.wheelOverlayContent.displayName.lastIndexOf('.'));
        return fullName.length > 20 ? fullName : '';
      }
      return '';
    },
    foregroundContentHint() {
      if (this.foregroundContent && this.foregroundContent.displayName) {
        const fullName = this.foregroundContent.displayName.substring(0, this.foregroundContent.displayName.lastIndexOf('.'));
        return fullName.length > 20 ? fullName : '';
      }
      return '';
    },
    segmentImageHint() {
      if (this.segmentImage && this.segmentImage.displayName) {
        const fullName = this.segmentImage.displayName.substring(0, this.segmentImage.displayName.lastIndexOf('.'));
        return fullName.length > 20 ? fullName : '';
      }
      return '';
    },
    segmentAnimationHint() {
      if (this.segmentAnimation && this.segmentAnimation.displayName) {
        const fullName = this.segmentAnimation.displayName.substring(0, this.segmentAnimation.displayName.lastIndexOf('.'));
        return fullName.length > 20 ? fullName : '';
      }
      return '';
    },
    segmentValueTypeOptions() {
      // Return keys from integrationValueTypes map
      if (this.getIntegrationValueTypes && Object.keys(this.getIntegrationValueTypes).length > 0) {
        return this.getIntegrationValueTypes.filter(it => it.value != 'custom');
      }
      return [];
    },
    isSegmentValueTypeDropdownDisabled() {
      // Disable if no integration name or no value types available
      return !this.getIntegrationValueTypes || 
             Object.keys(this.getIntegrationValueTypes).length === 0 ||
             this.disableField;
    },
  },
  methods: {
    ...mapActions([
      "addNewSegmentDefinition",
      "updateSegmentDefinitionName",
      "updateSegmentDefinitionGroup",
      "updateSegmentDefinitionBonus",
      "updateSegmentDefinitionValueType",
      "updateSegmentChoice",
      "updateSegmentImage",
      "updateSegmentAnimation",
      "updateForegroundContent",
      "updateForegroundContentSize",
      "updateForegroundRotation",
      "updateBackgroundContent",
      "updateBackgroundContentSize",
      "updateBackgroundRotation",
      "updateWheelOverlayContent",
      "updateWheelOverlayContentSize",
      "updateWheelOverlayRotation",
      "updateBackgroundOverlayContent",
      "updateBackgroundOverlayContentSize",
      "updateBackgroundOverlayRotation",
      "updateWinningAnimationSize",
      "updateWinningAnimationDuration",
      "updateWinningAnimationDelay",
      "getContentByPrefixAndTenant",
      "updateSelectedTier",
      "deleteSegmentDefinition",
      "updateUploadContentError",
      "clearSegmentOptions",
      "selectTenant",
      "addIntegrationValueType"
    ]),

    addSegmentType(){
      this.addIntegrationValueType({"name": this.customSegmentName, "value": this.customSegmentValue});
      this.addTypeDialog = false;
      this.customSegmentName = '';
      this.customSegmentValue = '';
    },

    searchForeground() {
      if (!this.searchForegroundTerm) {
        this.foregroundOptions = this.foregroundOptionsCopy;
      }
      
      this.foregroundOptions = this.foregroundOptionsCopy.filter((option) => {
        const displayName = option.displayName.substring(0, option.displayName.lastIndexOf('.'));
        return displayName.toLowerCase().indexOf(this.searchForegroundTerm.toLowerCase()) > -1;
      });
    },
    searchBackground() {
      if (!this.searchBackgroundTerm) {
        this.backgroundOptions = this.backgroundOptionsCopy;
      }

      this.backgroundOptions = this.backgroundOptionsCopy.filter((option) => {
        const displayName = option.displayName.substring(0, option.displayName.lastIndexOf('.'));
        return displayName.toLowerCase().indexOf(this.searchBackgroundTerm.toLowerCase()) > -1;
      });
    },
    searchWheelOverlay() {
      if (!this.searchWheelOverlayTerm) {
        this.wheelOverlayOptions = this.wheelOverlayOptionsCopy;
      }
      
      this.wheelOverlayOptions = this.wheelOverlayOptionsCopy.filter((option) => {
        const displayName = option.displayName.substring(0, option.displayName.lastIndexOf('.'));
        return displayName.toLowerCase().indexOf(this.searchWheelOverlayTerm.toLowerCase()) > -1;
      });
    },
    searchBackgroundOverlay() {
      if (!this.searchBackgroundOverlayTerm) {
        this.backgroundOverlayOptions = this.backgroundOverlayOptionsCopy;
      }

      this.backgroundOverlayOptions = this.backgroundOverlayOptionsCopy.filter((option) => {
        const displayName = option.displayName.substring(0, option.displayName.lastIndexOf('.'));
        return displayName.toLowerCase().indexOf(this.searchBackgroundOverlayTerm.toLowerCase()) > -1;
      });
    },
    searchSegmentImage() {
      if (!this.searchSegmentImageTerm) {
        this.segmentImageOptions = this.segmentImageOptionsCopy;
      }
      
      this.segmentImageOptions = this.segmentImageOptionsCopy.filter((option) => {
        const displayName = option.displayName.substring(0, option.displayName.lastIndexOf('.'));
        return displayName.toLowerCase().indexOf(this.searchSegmentImageTerm.toLowerCase()) > -1;
      });
    },
    searchSegmentAnimation() {
      if (!this.searchSegmentAnimationTerm) {
        this.segmentAnimationOptions = this.segmentAnimationOptionsCopy;
      }
      
      this.segmentAnimationOptions = this.segmentAnimationOptionsCopy.filter((option) => {
        const displayName = option.displayName.substring(0, option.displayName.lastIndexOf('.'));
        return displayName.toLowerCase().indexOf(this.searchSegmentAnimationTerm.toLowerCase()) > -1;
      });
    },
    tierChipClass(index) {
      return index == this.selectedTier ? 'primary' : ''
    },
    setBonusSegment(bonusSegment) {
      if (!this.getSelectedGame || !this.getSelectedGame.tiers || this.getSelectedGame.tiers.length == 0) {
        return;
      }
      this.updateSegmentDefinitionBonus({
        bonusSegment: bonusSegment,
        index: this.selectedSegmentDefinition,
        tier: this.selectedTier,
      });
    },
    numberBetweenOneAndHundred(value) {
      if (value === null || value == '') {
        return true; // allow empty value
      }
      if (!(/^[0-9]*$/.test(value))) {
        return 'Value must be a number between 1 and 100';
      }
      const num = parseFloat(value);
      return !isNaN(num) && num >= 1 && num <= 100 || 'Value must be a number between 1 and 100';
    },
    numberGreaterThanZero(value) {
      if (value === null || value == '') {
        return true; // allow empty value
      }
      if (!(/^[0-9]*$/.test(value))) {
        return 'Value must be a number greater than 0';
      }
      const num = parseFloat(value);
      return !isNaN(num) && num >= 0 || 'Value must be a number greater than 0';
    },
    isContentFilesValid(file) {
      if (!file) {
        this.$toast.error("No file provided");
        return false;
      }
      if (!file.name || typeof file.name !== 'string') {
        this.$toast.error("Invalid file name");
        return false;
      }
      if (!file.size || file.size <= 0) {
        this.$toast.error("File is empty or corrupted");
        return false;
      }
      if (file.size > 100000000) {
        this.$toast.error("File size should not exceed 100 MB");
        return false;
      }
      if (!file.type.includes("image") && !file.name.includes(".webm") && !file.name.includes(".mp4") && !file.name.includes(".svg")) {
        this.$toast.error("File format must be an image, webm or mp4");
        return false;
      }
      if (file.name.includes(".gif")) {
        this.$toast.error("File must not be a GIF");
        return false;
      }
      return true;
    },
    isSegmentImageFileValid(file) {
      if (!file) {
        this.$toast.error("No file provided");
        return false;
      }
      if (!file.name || typeof file.name !== 'string') {
        this.$toast.error("Invalid file name");
        return false;
      }
      if (!file.size || file.size <= 0) {
        this.$toast.error("File is empty or corrupted");
        return false;
      }
      if (file.size > 100000000) {
        this.$toast.error("File size should not exceed 100 MB");
        return false;
      }
      if (!file.type || !file.type.includes("image")) {
        this.$toast.error("File must be an image");
        return false;
      }
      if (file.name.includes(".gif")) {
        this.$toast.error("File must not be a GIF");
        return false;
      }
      return true;
    },
    deleteSelectedSegmentDefinition() {
      this.deleteSegmentDialog = false;
      if (this.selectedTier == 0 && this.getSelectedGame.segment_definitions.length == 3) {
        return;
      } 
      if (!this.selectedTier == 0 && this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions.length == 3) {
        return;
      }      
      this.deleteSegmentDefinition({
        index: this.selectedSegmentDefinition,
        tier: this.selectedTier,
      });
      this.selectedSegmentDefinition = 0;
    },
    triggerSegmentUpdateConfirmation(segmentChoice) {
      this.segmentToUpdate = segmentChoice;
      if(this.segmentChoice == null || this.segmentChoice == 0){
        this.updateSegmentCount();
      }
      else{
        this.warningDialog = true;
      }
    },
    async updateSegmentCount() {
      this.selectedSegmentDefinition = 0;
      this.updateSegmentChoice({
        noOfSegments: this.segmentToUpdate,
        tierIndex: this.selectedTier,
      });
      await this.getContentByPrefixAndTenant({prefix:`${this.getTenant}/segment/${this.segmentToUpdate}`, tenantId: this.getTenant, update:"updateSegmentOptions"});
      this.segmentImageOptions = [...this.getSegmentOptions].reverse();
      this.segmentImageOptionsCopy = this.segmentImageOptions;
      this.segmentToUpdate = null;
      this.warningDialog = false;
    },
    async changeSelectedTier(noOfSegments) {
      this.selectedSegmentDefinition = 0;
      this.clearSegmentOptions();
      await this.getContentByPrefixAndTenant({prefix:`${this.getTenant}/segment/${noOfSegments}`, tenantId: this.getTenant, update:"updateSegmentOptions"});
      this.segmentImageOptions = [...this.getSegmentOptions].reverse();
      this.segmentImageOptionsCopy = this.segmentImageOptions;
    },
    cancelSegmentUpdate() {
      this.segmentToUpdate = null;
      this.warningDialog = false;
    },
    selectSegmentDefinition(index) {
      if (!index) return;
      this.selectedSegmentDefinition = index;
    },
    async onContentFilesUploaded() {      
      for (let i = 0; i < this.contentFiles.length; i++) {
        if (!this.isContentFilesValid(this.contentFiles[i])) {
          this.contentFiles = [];
          return;
        }
      }

      this.contentUploading = true;
      this.contentUploadProgress = 0;
      this.contentUploadedCount = 0;

      for (let i = 0; i < this.contentFiles.length; i++) {
        const file = this.contentFiles[i];
        const reader = new FileReader();

        await new Promise((resolve, reject) => {
          reader.onload = async () => {
            try {
              const base64 = reader.result;

              await this.$store.dispatch("uploadContent", {
                type: "animation",
                displayName: file.name,
                prefix: `${this.getTenant}/content`,
                file: file,
                base64: base64,
                commitMethod: "setContentOptions",
              });
              await this.getContentByPrefixAndTenant({prefix:`${this.getTenant}/content`, tenantId: this.getTenant, update:"updateContentOptions"});

              this.contentUploadedCount++;
              this.contentUploadProgress = Math.round(
                ((i + 1) / this.contentFiles.length) * 100
              );
              this.setOptionsDropdowns();
              this.searchForeground();
              this.searchWheelOverlay();
              this.searchBackground();
              this.searchBackgroundOverlay();
              resolve();
            } catch (error) {
              console.error('Error uploading content:', error);
              reject(error);
            } finally {
              reader.onload = null;
              reader.onerror = null;
            }
          };
          
          reader.onerror = () => {
            console.error('Error reading file:', file.name);
            reader.onload = null;
            reader.onerror = null;
            reject(new Error(`Failed to read file: ${file.name}`));
          };
          
          reader.readAsDataURL(file);
        });
      }

      this.contentUploading = false;
      this.contentUploadProgress = 0;
      this.contentUploadedCount = 0;
    },
    handleContentDragOver(event) {
      event.preventDefault();
    },
    handleContentDrop(event) {
      event.preventDefault();
      const files = event.dataTransfer.files;
      this.contentFiles = [...this.contentFiles, ...files];
      this.onContentFilesUploaded();
    },
    async onSegmentContentUploaded() {
      if (!this.segmentFiles || !Array.isArray(this.segmentFiles) || this.segmentFiles.length === 0) {
        this.$toast.error("No segment files selected");
        return;
      }
      
      for (let i = 0; i < this.segmentFiles.length; i++) {
        if (!this.isSegmentImageFileValid(this.segmentFiles[i])) {
          this.segmentFiles = [];
          return;
        }
      }
      this.segmentUploading = true;
      this.segmentUploadProgress = 0;
      this.segmentUploadedCount = 0;

      for (let i = 0; i < this.segmentFiles.length; i++) {
        const file = this.segmentFiles[i];
        const segmentCount = this.segmentChoice;
        const reader = new FileReader();

        await new Promise((resolve, reject) => {
          reader.onload = async () => {
            try {
              const base64 = reader.result;

              await this.$store.dispatch("uploadContent", {
                type: `${segmentCount}_segment`,
                displayName: file.name,
                prefix: `${this.getTenant}/segment/${segmentCount}`,
                file: file,
                base64: base64,
                commitMethod: "setSegmentOptions",
              });
              await this.getContentByPrefixAndTenant({prefix:`${this.getTenant}/segment/${segmentCount}`, tenantId: this.getTenant, update:"updateSegmentOptions"});

              this.segmentUploadedCount++;
              this.segmentUploadProgress = Math.round(((i + 1) / this.segmentFiles.length) * 100);
              this.segmentImageOptions = [...this.getSegmentOptions].reverse();
              this.segmentImageOptionsCopy = this.segmentImageOptions;
              this.searchSegmentImage();
              resolve();
            } catch (error) {
              console.error('Error uploading segment content:', error);
              reject(error);
            } finally {
              reader.onload = null;
              reader.onerror = null;
            }
          };
          
          reader.onerror = () => {
            console.error('Error reading segment file:', file.name);
            reader.onload = null;
            reader.onerror = null;
            reject(new Error(`Failed to read segment file: ${file.name}`));
          };
          
          reader.readAsDataURL(file);
        });
      }

      this.segmentUploading = false;
      this.segmentUploadProgress = 0;
      this.segmentUploadedCount = 0;
    },
    handleSegmentContentDragOver(event) {
      event.preventDefault();
    },
    handleSegmentContentDrop(event) {
      event.preventDefault();
      const files = event.dataTransfer.files;
      this.segmentFiles = [...this.segmentFiles, ...files];
      this.onSegmentContentUploaded();
    },
    async onSegmentAnimationsUploaded() {
      if (!this.segmentAnimationFiles || !Array.isArray(this.segmentAnimationFiles) || this.segmentAnimationFiles.length === 0) {
        this.$toast.error("No animation files selected");
        return;
      }
      
      for (let i = 0; i < this.segmentAnimationFiles.length; i++) {
        if (!this.isContentFilesValid(this.segmentAnimationFiles[i])) {
          this.segmentAnimationFiles = [];
          return;
        }
      }
      this.segmentAnimationUploading = true;
      this.segmentAnimationUploadProgress = 0;
      this.segmentAnimationUploadedCount = 0;

      for (let i = 0; i < this.segmentAnimationFiles.length; i++) {
        const file = this.segmentAnimationFiles[i];
        const reader = new FileReader();

        await new Promise((resolve, reject) => {
          reader.onload = async () => {
            try {
              const base64 = reader.result;

              await this.$store.dispatch("uploadContent", {
                type: "animation",
                displayName: file.name,
                prefix: `${this.getTenant}/segment/winning-animation`,
                file: file,
                base64: base64,
                commitMethod: "setWinningAnimationsOptions",
              });
              await this.getContentByPrefixAndTenant({prefix:`${this.getTenant}/segment/winning-animation`, tenantId: this.getTenant, update:"updateWinningSegmentAnimations"})

              this.segmentAnimationUploadedCount++;
              this.segmentAnimationUploadProgress = Math.round(((i + 1) / this.segmentAnimationFiles.length) * 100);
              this.segmentAnimationOptions = [...this.getWinningAnimationsOptions].reverse();
              this.segmentAnimationOptionsCopy = this.segmentAnimationOptions;
              this.searchSegmentAnimation();
              resolve();
            } catch (error) {
              console.error('Error uploading segment animation:', error);
              reject(error);
            } finally {
              reader.onload = null;
              reader.onerror = null;
            }
          };
          
          reader.onerror = () => {
            console.error('Error reading animation file:', file.name);
            reader.onload = null;
            reader.onerror = null;
            reject(new Error(`Failed to read animation file: ${file.name}`));
          };
          
          reader.readAsDataURL(file);
        });
      }

      this.segmentAnimationUploading = false;
      this.segmentAnimationUploadProgress = 0;
      this.segmentAnimationUploadedCount = 0;
    },
    handleSegmentAnimationDragOver(event) {
      event.preventDefault();
    },
    handleSegmentAnimationDrop(event) {
      event.preventDefault();
      const files = event.dataTransfer.files;
      this.segmentAnimationFiles = [...this.segmentAnimationFiles, ...files];
      this.onSegmentAnimationsUploaded();
    },
    selectSegmentImage(item) {
      this.updateSegmentImage({
        tier: this.selectedTier,
        index: this.selectedSegmentDefinition,
        image: item,
      });
    },
    selectSegmentAnimation(item) {
      this.updateSegmentAnimation({
        tier: this.selectedTier,
        index: this.selectedSegmentDefinition,
        animation: item,
      });
    },
    addSegment() {
      if (this.selectedTier == 0 && this.getSelectedGame.segment_definitions.length == this.getSelectedGame.segments.length) {
        return;
      } else if (!this.selectedTier == 0 && this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions.length == this.getSelectedGame.tiers[this.selectedTier - 1].segments.length) {
         return;
      }
      this.addNewSegmentDefinition({ tier: this.selectedTier });
      this.selectedSegmentDefinition = this.selectedTier == 0 ? this.getSelectedGame.segment_definitions.length - 1 : this.getSelectedGame.tiers[this.selectedTier - 1].segment_definitions.length - 1;
    },
    previousStep() {
      this.$emit("back");
    },
    cancel() {
      this.$emit("cancel");
    },
    assetNameFormatted(item) {
      if (item == null || item.displayName == null) return '';
      return item.displayName.substring(0, item.displayName.lastIndexOf('.'));
    },
    setOptionsDropdowns() {
      this.foregroundOptions = [...this.getContentOptions].reverse();
      this.foregroundOptionsCopy = this.foregroundOptions;

      this.backgroundOptions = [...this.getContentOptions].reverse();
      this.backgroundOptionsCopy = this.backgroundOptions;
      const reversedArray = [...this.getContentOptions].reverse();
      for (let i = 0; i < reversedArray.length; i++) {
        if (reversedArray[i].displayName == "black_background.png") {
          let blackBackground = Object.assign({}, reversedArray[i]);
          reversedArray.splice(i, 1);
          reversedArray.unshift(blackBackground)
        }
        if (reversedArray[i].displayName == "white_background.png") {
          let whiteBackground = Object.assign({}, reversedArray[i]);
          reversedArray.splice(i, 1);
          reversedArray.unshift(whiteBackground)
        }
      }
      this.backgroundOptions = reversedArray;
      this.backgroundOptionsCopy = this.backgroundOptions;

      this.wheelOverlayOptions = [...this.getContentOptions].reverse();
      this.wheelOverlayOptionsCopy = this.wheelOverlayOptions;
      this.backgroundOverlayOptions = [...this.getContentOptions].reverse();
      this.backgroundOverlayOptionsCopy = this.backgroundOverlayOptions;

      this.segmentImageOptions = [...this.getSegmentOptions].reverse();
      this.segmentImageOptionsCopy = this.segmentImageOptions;
      this.segmentAnimationOptions = [...this.getWinningAnimationsOptions].reverse();
      this.segmentAnimationOptionsCopy = this.segmentAnimationOptions;
    }
  },
  watch: {
    getUploadContentError(newError) {
      if (newError == "null") return;
        this.$toast.error(newError);
        this.updateUploadContentError("null");
    },
    segmentGroup() {
      this.segmentGroupKey++;
    },
    customSegmentText(){
      this.updateSegmentDefinitionValueType({
        segmentValueType: { "name": "Custom", "value" : this.segmentValueTypeText },
        index: this.selectedSegmentDefinition,
        tier: this.selectedTier,
      });
    }
  }
};
</script>

<style scoped>
.roundBorder {
  border: 1px solid black;
  border-radius: 1em;
}

.form_container {
  overflow-y: scroll; /* add scroll when the height exceeds */
}

.form_container::-webkit-scrollbar {
  display: none;
}

.file_upload ::v-deep .v-input__slot {
  height: 90px !important;
  width: 100%;
  background: #f8f8f8 !important;
  border-radius: 15px;
  overflow: visible !important;
}

.file_upload ::v-deep .v-label{
  color: #808080;
}

.file_upload ::v-deep .v-file-input__text {
  overflow: auto;
  padding-top: 8px !important;
  margin-top: 4px !important;
}

.v-file-input__slot::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 24px;
  color: #c4c4c4;
  font-family: "Material Design Icons";
  font-weight: normal;
  font-style: normal;
  font-display: block;
  text-transform: none;
  line-height: 1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.active{
    background: black !important;
    color: white !important;
}
#yesBonusButton.yes_button_nobonus.v-btn{
    background: #E2FBE0 !important;
    opacity: 100% !important;
    padding-right: 10px !important;
    margin-right: -4px;
    border-width: thin;
    border-color: #64B65C !important;
}
.yes_button_bonus.v-btn{
    opacity: 100% !important;
    border-top-right-radius: inherit !important;
    border-bottom-right-radius: inherit !important;
    z-index: 1;
    background: black !important;
    color: white !important;
}
.no_button_nobonus.v-btn{
    opacity: 100% !important;
    border-top-left-radius: inherit !important;
    border-bottom-left-radius: inherit !important;
    z-index: 1;
    background: black !important;
    color: white !important;
}
#noBonusButton.no_button_bonus.v-btn{
    background: #F9CFCF !important;
    opacity: 100% !important;
    padding-left: 10px !important;
    margin-left: -3px;
    border-width: thin;
    border-color: #F25858 !important;
}
.segment_value_tooltip_width {
  max-width: 800px;
}

/* Expansion Panel Styling */
.v-expansion-panels {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.v-expansion-panel {
  background-color: #ffffff;
  border: none !important;
}

.v-expansion-panel:not(:last-child) > .v-expansion-panel-header {
  border-bottom: 1px solid #e0e0e0 !important;
}

.v-expansion-panel-header {
  background-color: #fafafa;
  color: #333333;
}

.v-expansion-panel-header:hover {
  background-color: #f5f5f5;
}

.v-expansion-panel--active {
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  position: relative;
  z-index: 2;
  border: 1px solid #e0e0e0 !important;
}

.v-expansion-panel--active > .v-expansion-panel-header {
  background-color: #fafafa;
  border-bottom: 1px solid #e0e0e0 !important;
}

.v-expansion-panel-content__wrap {
  padding: 20px 24px;
}
</style>
